import type { Dispatch } from 'react';

import { AddToCart } from '@/lib/functions/addToCart';
import { clearCheckoutFormDraft } from '@/lib/functions/checkoutFormDraft';
import { getOrderByToken } from '@/lib/functions/getOrderByToken';
import { getOrderToken } from '@/lib/functions/getOrderToken';
import { logMessage } from '@/lib/functions/logMessage';
import { persistOrderState } from '@/lib/functions/persistOrderState';
import { publishCartRefresh } from '@/lib/broadcast/cartSync';

import type { OrderActions } from '@/lib/context/orderActions';
import type { OrderPayloadItem } from '@/lib/types/orderPayload';
import type { OrderState } from '@/lib/types/orderState';

export interface RecreateCartWithItemsResult {
  success: boolean;
  token?: string | null;
  failedVariantCodes?: string[];
}

const recreateInFlight = new Map<string, Promise<RecreateCartWithItemsResult>>();

/**
 * Creates a fresh guest cart and re-adds line items from the current cart.
 * Used on logout so the old cart (and its shipping address) is abandoned.
 */
export async function recreateCartWithItems(
  orderState: OrderState,
  orderDispatch: Dispatch<OrderActions>,
): Promise<RecreateCartWithItemsResult> {
  const oldToken = orderState.token;
  if (!oldToken) {
    return { success: true, token: null };
  }

  const inFlight = recreateInFlight.get(oldToken);
  if (inFlight) {
    return inFlight;
  }

  const promise = recreateCartWithItemsInternal(orderState, orderDispatch, oldToken);
  recreateInFlight.set(oldToken, promise);

  try {
    return await promise;
  } finally {
    if (recreateInFlight.get(oldToken) === promise) {
      recreateInFlight.delete(oldToken);
    }
  }
}

async function recreateCartWithItemsInternal(
  orderState: OrderState,
  orderDispatch: Dispatch<OrderActions>,
  oldToken: string,
): Promise<RecreateCartWithItemsResult> {
  try {
    const orderData = await getOrderByToken(orderState, undefined, oldToken);
    const items: OrderPayloadItem[] = orderData?.items ?? [];

    const tokenResp = await getOrderToken(orderState, orderDispatch, true);
    if (!tokenResp.okay || !tokenResp.token) {
      return { success: false, token: oldToken };
    }

    const newToken = tokenResp.token;
    if (newToken === oldToken) {
      logMessage('Cart recreation returned the same token; expected a new cart.', 'warning');
      return { success: false, token: oldToken };
    }

    const guestCartState: OrderState = {
      ...orderState,
      token: newToken,
      tokenType: 'guest',
      itemCount: 0,
    };
    persistOrderState(guestCartState);

    clearCheckoutFormDraft(oldToken);

    if (items.length === 0) {
      publishCartRefresh(newToken);
      return { success: true, token: newToken };
    }

    const failedVariantCodes: string[] = [];
    const cartState: OrderState = { ...guestCartState };

    for (const item of items) {
      if (!item.variantCode || item.quantity < 1) {
        continue;
      }

      const result = await AddToCart(
        item.variantCode,
        item.quantity,
        cartState,
        orderDispatch,
      );

      if (!result.success) {
        failedVariantCodes.push(item.variantCode);
        logMessage(
          `Failed to re-add ${item.variantCode} while recreating cart: ${result.message}`,
          'warning',
        );
      }
    }

    publishCartRefresh(newToken);

    return {
      success: failedVariantCodes.length === 0,
      token: newToken,
      ...(failedVariantCodes.length > 0 ? { failedVariantCodes } : {}),
    };
  } catch (err) {
    logMessage(
      `Cart recreation failed: ${err instanceof Error ? err.message : String(err)}`,
      'error',
    );
    return { success: false, token: oldToken };
  }
}
